import React, { useState, useEffect } from "react";
import axios from "axios";
import { AlertTriangle, Plus, X, Clock } from "lucide-react";
import { useTheme } from "context/ThemeContext";

import { apiBaseUrl } from '../../../services/config';
const API = apiBaseUrl;
const ax  = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` }
});

const getDaysInMonth = (yearMonth) => {
  const [year, month] = yearMonth.split("-").map(Number);
  const count = new Date(year, month, 0).getDate();
  const days  = [];
  for (let d = 1; d <= count; d++)
    days.push(`${yearMonth}-${String(d).padStart(2, "0")}`);
  return days;
};

const parseDate = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const fmtTime = (t) => {
  if (!t) return null;
  const [h, m] = t.slice(0, 5).split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12  = h % 12 || 12;
  return { h: String(h12).padStart(2, "0"), m: String(m).padStart(2, "0"), ampm };
};

const nowHHMM = () => {
  const n = new Date();
  return `${String(n.getHours()).padStart(2,"0")}:${String(n.getMinutes()).padStart(2,"0")}`;
};

// ── Custom drum-style time picker ─────────────────────────────────
function TimePicker({ value, onChange }) {
  const { T } = useTheme();

  const parse = (v) => {
    if (!v) { const n = new Date(); const h = n.getHours();
      return { h12: String(h % 12 || 12).padStart(2,"0"), m: String(n.getMinutes()).padStart(2,"0"), ampm: h >= 12 ? "PM" : "AM" }; }
    const [h, m] = v.split(":").map(Number);
    return { h12: String(h % 12 || 12).padStart(2,"0"), m: String(m).padStart(2,"0"), ampm: h >= 12 ? "PM" : "AM" };
  };

  const { h12, m, ampm } = parse(value);

  const emit = (newH12, newM, newAmpm) => {
    let h24 = parseInt(newH12) % 12;
    if (newAmpm === "PM") h24 += 12;
    onChange(`${String(h24).padStart(2,"0")}:${newM}`);
  };

  const hours   = Array.from({length:12}, (_, i) => String(i + 1).padStart(2,"0"));
  const minutes = Array.from({length:12}, (_, i) => String(i * 5).padStart(2,"0"));

  const Drum = ({ options, val, onSelect }) => {
    const idx = options.indexOf(val);
    const prev = options[(idx - 1 + options.length) % options.length];
    const next = options[(idx + 1) % options.length];
    return (
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
        <button type="button" onClick={() => onSelect(prev)}
          style={{ background:"none", border:"none", cursor:"pointer", color:T.muted,
            fontSize:15, fontWeight:700, lineHeight:1, padding:"2px 8px", borderRadius:4 }}>▲</button>
        <div style={{ width:44, height:36, display:"flex", alignItems:"center", justifyContent:"center",
          background:T.inp_bg, border:`1.5px solid ${T.cyan}40`, borderRadius:8,
          fontSize:17, fontWeight:800, fontFamily:"JetBrains Mono,monospace",
          color:T.text, letterSpacing:"0.04em" }}>
          {val}
        </div>
        <button type="button" onClick={() => onSelect(next)}
          style={{ background:"none", border:"none", cursor:"pointer", color:T.muted,
            fontSize:15, fontWeight:700, lineHeight:1, padding:"2px 8px", borderRadius:4 }}>▼</button>
      </div>
    );
  };

  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center",
      gap:7, padding:"10px 12px", background:T.toggle_bg,
      borderRadius:10, border:`1px solid ${T.border}` }}>
      <Drum options={hours}   val={h12} onSelect={v => emit(v, m, ampm)}/>
      <span style={{ fontSize:19, fontWeight:900, color:T.muted, marginBottom:2 }}>:</span>
      <Drum options={minutes} val={m}   onSelect={v => emit(h12, v, ampm)}/>
      <div style={{ display:"flex", flexDirection:"column", gap:5, marginLeft:4 }}>
        {["AM","PM"].map(ap => (
          <button key={ap} type="button" onClick={() => emit(h12, m, ap)}
            style={{ padding:"6px 10px", borderRadius:7, border:"none", cursor:"pointer",
              fontFamily:"Outfit,sans-serif", fontSize:11, fontWeight:800,
              letterSpacing:"0.05em", transition:"all 0.15s",
              background: ampm === ap
                ? (ap === "AM" ? `linear-gradient(135deg,${T.cyan},${T.green})` : `linear-gradient(135deg,${T.amber},${T.red})`)
                : T.inp_bg,
              color:   ampm === ap ? "#fff" : T.dim,
              boxShadow: ampm === ap ? `0 2px 6px ${ap==="AM"?T.cyan:T.amber}35` : "none",
            }}>{ap}</button>
        ))}
      </div>
    </div>
  );
}

export default function EmployeeAttendance() {
  const { T } = useTheme();

  const STATUS_COLOR = {
    Present:   { color:T.green,  bg:`${T.green}1A`  },
    Absent:    { color:T.red,    bg:`${T.red}1A`    },
    Late:      { color:T.amber,  bg:`${T.amber}1A`  },
    "Half Day":{ color:T.cyan,   bg:`${T.cyan}1A`   },
    Holiday:   { color:T.purple, bg:`${T.purple}1A` },
    Leave:     { color:T.muted,  bg:`${T.muted}1A`  },
    WFH:       { color:T.cyan,   bg:`${T.cyan}1A`   },
  };

  const REG_STATUS = {
    Approved: { color:T.green, bg:`${T.green}1A` },
    Pending:  { color:T.amber, bg:`${T.amber}1A` },
    Rejected: { color:T.red,   bg:`${T.red}1A`   },
  };

  const userData   = JSON.parse(sessionStorage.getItem("platform_user") || "{}");
  const platformId = userData.id;
  const today      = new Date();
  const todayStr   = today.toISOString().split("T")[0];

  const [month,      setMonth]      = useState(today.toISOString().slice(0,7));
  const [records,    setRecords]    = useState([]);
  const [regs,       setRegs]       = useState([]);
  const [summary,    setSummary]    = useState({});
  const [loading,    setLoading]    = useState(true);
  const [regOpen,    setRegOpen]    = useState(false);
  const [toast,      setToast]      = useState("");
  const [form,       setForm]       = useState({ date:"", requested_in:"", requested_out:"", reason:"" });
  const [submitting, setSubmitting] = useState(false);
  const [regTab,     setRegTab]     = useState(false);
  const [todayRec,   setTodayRec]   = useState(null);
  const [punching,   setPunching]   = useState(false);
  // punch time-picker modal
  const [punchModal, setPunchModal] = useState(null);   // null | "in" | "out"
  const [punchTime,  setPunchTime]  = useState("");

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3500); };

  const load = async () => {
    if (!platformId) return;
    setLoading(true);
    try {
      const [aRes, rRes] = await Promise.all([
        axios.get(`${API}/employee-portal/attendance?platform_user_id=${platformId}&month=${month}`, ax()),
        axios.get(`${API}/attendance-regularization/my?platform_user_id=${platformId}`, ax()),
      ]);
      const recs = aRes.data?.records || aRes.data || [];
      setRecords(recs);
      setSummary(aRes.data?.summary || {});
      setRegs(rRes.data || []);
      setTodayRec(recs.find(r => r.date === todayStr) || null);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [platformId, month]);

  const openPunch = (type) => {
    setPunchTime(nowHHMM());
    setPunchModal(type);
  };

  const confirmPunch = async () => {
    if (!platformId || !punchModal) return;
    setPunching(true);
    setPunchModal(null);
    try {
      const endpoint = punchModal === "in" ? "checkin" : "checkout";
      const body     = punchModal === "in" ? { check_in: punchTime } : { check_out: punchTime };
      const res = await axios.post(
        `${API}/employee-portal/${endpoint}?platform_user_id=${platformId}`,
        body, ax()
      );
      const time = punchModal === "in" ? res.data?.check_in : res.data?.check_out;
      flash(punchModal === "in" ? `✅ Checked in at ${time || punchTime}` : `✅ Checked out at ${time || punchTime}`);
      load();
    } catch(e) {
      flash("❌ " + (e.response?.data?.detail || "Punch failed"));
    } finally { setPunching(false); }
  };

  const openReg = (date = "") => {
    setForm({ date, requested_in: nowHHMM(), requested_out:"", reason:"" });
    setRegOpen(true);
  };

  const submitReg = async () => {
    if (!form.date || !form.reason) { flash("❌ Date and reason are required"); return; }
    if (!form.requested_in && !form.requested_out) { flash("❌ Enter at least check-in or check-out time"); return; }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/attendance-regularization/?platform_user_id=${platformId}`,
        form, ax()
      );
      flash("✅ Regularization request submitted to your manager");
      setRegOpen(false);
      load();
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Error")); }
    finally { setSubmitting(false); }
  };

  const inp  = { background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:13, padding:"9px 12px", outline:"none", fontFamily:"Outfit,sans-serif", width:"100%", boxSizing:"border-box" };
  const card = { background:T.card, border:`1px solid ${T.border}`, borderRadius:12 };

  const pendingRegs = regs.filter(r => r.status === "Pending");
  const hasIn  = !!todayRec?.check_in;
  const hasOut = !!todayRec?.check_out;
  const allDays = getDaysInMonth(month);

  const th = {
    padding:"11px 16px", textAlign:"left", fontSize:11, fontWeight:700,
    color:T.muted, textTransform:"uppercase", letterSpacing:"0.07em",
    borderBottom:`1px solid ${T.border}`, background:T.card2, whiteSpace:"nowrap",
  };

  return (
    <div style={{ fontFamily:"'Outfit',sans-serif", color:T.text }}>

      {/* ── Header ── */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
        <div>
          <div style={{ fontSize:11, color:T.cyan, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>ATTENDANCE & LEAVE</div>
          <h2 style={{ fontSize:22, fontWeight:800, margin:"0 0 4px" }}>My Attendance</h2>
          <p style={{ fontSize:13, color:T.muted, margin:0 }}>Mark today's attendance · request corrections for past days</p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            style={{ background:T.inp_bg, border:`1px solid ${T.cyan}1F`, borderRadius:8, color:T.text, fontSize:13, padding:"8px 12px", outline:"none", fontFamily:"Outfit,sans-serif" }}/>
          <button onClick={() => openReg()}
            style={{ display:"flex", alignItems:"center", gap:7, background:`${T.amber}1A`, border:`1px solid ${T.amber}40`, borderRadius:8, color:T.amber, fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:600, padding:"8px 14px", cursor:"pointer" }}>
            <Plus size={13}/> Request Correction
          </button>
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))", gap:12, marginBottom:20 }}>
        {[
          { label:"Present",    val:summary.present  ?? records.filter(r=>r.status==="Present").length,  color:T.green  },
          { label:"Absent",     val:summary.absent   ?? records.filter(r=>r.status==="Absent").length,   color:T.red    },
          { label:"Late",       val:summary.late     ?? records.filter(r=>r.status==="Late").length,     color:T.amber  },
          { label:"Leave",      val:summary.leave    ?? records.filter(r=>r.status==="Leave").length,    color:T.muted  },
          { label:"Avg Hours",  val:`${summary.avg_hours || 0}h`,                                        color:T.cyan   },
          { label:"Corrections",val:pendingRegs.length,                                                  color:T.red    },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ ...card, padding:14, borderTop:`3px solid ${color}` }}>
            <div style={{ fontSize:22, fontWeight:900, fontFamily:"JetBrains Mono,monospace", color }}>{val}</div>
            <div style={{ fontSize:10.5, color:T.muted, textTransform:"uppercase", letterSpacing:"0.07em", marginTop:4 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── Pending correction notice ── */}
      {pendingRegs.length > 0 && (
        <div style={{ ...card, padding:"12px 18px", marginBottom:16, display:"flex", gap:10, alignItems:"center", background:`${T.amber}0A`, borderColor:`${T.amber}26` }}>
          <AlertTriangle size={14} style={{ color:T.amber, flexShrink:0 }}/>
          <span style={{ fontSize:12.5, color:T.muted }}>
            You have <strong style={{ color:T.amber }}>{pendingRegs.length}</strong> pending correction request{pendingRegs.length>1?"s":""} awaiting manager review.
          </span>
          <button onClick={() => setRegTab(t=>!t)} style={{ marginLeft:"auto", fontSize:12, color:T.amber, background:"none", border:"none", cursor:"pointer", fontFamily:"Outfit,sans-serif", textDecoration:"underline" }}>
            {regTab ? "Hide" : "View"} requests
          </button>
        </div>
      )}

      {/* ── My regularization requests ── */}
      {regTab && regs.length > 0 && (
        <div style={{ ...card, marginBottom:16 }}>
          <div style={{ padding:"12px 18px", borderBottom:`1px solid ${T.border}`, fontSize:13, fontWeight:600 }}>My Correction Requests</div>
          {regs.map(r => {
            const sc = REG_STATUS[r.status] || REG_STATUS.Pending;
            return (
              <div key={r.id} style={{ padding:"12px 18px", borderBottom:`1px solid ${T.toggle_bg}`, display:"flex", gap:12, alignItems:"center" }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, fontFamily:"JetBrains Mono,monospace", color:T.text }}>{r.date}</div>
                  <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>
                    Requested: {r.requested_in||"—"} → {r.requested_out||"—"}
                    {r.existing_in && <span style={{ color:T.dim, marginLeft:8 }}>(was: {r.existing_in||"—"} → {r.existing_out||"—"})</span>}
                  </div>
                  <div style={{ fontSize:12, color:T.dim, marginTop:2 }}>{r.reason}</div>
                  {r.review_remarks && <div style={{ fontSize:12, color:T.amber, marginTop:2 }}>Manager: {r.review_remarks}</div>}
                </div>
                <span style={{ fontSize:11.5, padding:"3px 10px", borderRadius:20, fontWeight:600, background:sc.bg, color:sc.color }}>{r.status}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Attendance Table ── */}
      <div style={card}>
        <div style={{ padding:"14px 20px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <h3 style={{ margin:0, fontSize:15, fontWeight:600 }}>Daily Records — {month}</h3>
          <span style={{ fontSize:12, color:T.muted }}>{allDays.length} days</span>
        </div>

        {loading ? (
          <div style={{ padding:48, textAlign:"center", color:T.muted, fontSize:13 }}>Loading…</div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontFamily:"Outfit,sans-serif" }}>
              <thead>
                <tr>
                  <th style={th}>Date</th>
                  <th style={th}>Status</th>
                  <th style={th}>Check In</th>
                  <th style={th}>Check Out</th>
                  <th style={th}>Hours</th>
                  <th style={th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {allDays.map(date => {
                  const rec       = records.find(r => r.date === date);
                  const isToday   = date === todayStr;
                  const isPast    = date < todayStr;
                  const isFuture  = date > todayStr;
                  const sc        = STATUS_COLOR[rec?.status] || null;
                  const hasReg    = regs.find(reg => reg.date === date && reg.status === "Pending");
                  const dateObj   = parseDate(date);
                  const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
                  const tIn       = fmtTime(rec?.check_in);
                  const tOut      = fmtTime(rec?.check_out);

                  const timeChip = (t, color) => (
                    <span style={{ display:"inline-flex", alignItems:"center", gap:5,
                      fontSize:13, fontWeight:700, fontFamily:"JetBrains Mono,monospace",
                      color }}>
                      <Clock size={11} style={{ flexShrink:0 }}/>
                      {t.h}:{t.m} <span style={{ fontSize:10, opacity:0.75 }}>{t.ampm}</span>
                    </span>
                  );

                  const punchBtn = (type, label, enabled) => (
                    <button
                      onClick={enabled ? () => openPunch(type) : undefined}
                      disabled={!enabled || punching}
                      style={{ padding:"6px 14px", borderRadius:7, fontWeight:600,
                        fontFamily:"Outfit,sans-serif", fontSize:12,
                        border: enabled ? "none" : `1px solid ${T.border}`,
                        background: enabled
                          ? (type === "in" ? `linear-gradient(135deg,${T.green},${T.cyan})` : `linear-gradient(135deg,${T.red},${T.amber})`)
                          : T.toggle_bg,
                        color:  enabled ? "#fff" : T.dim,
                        cursor: enabled ? "pointer" : "not-allowed",
                        opacity: punching ? 0.6 : 1 }}>
                      {label}
                    </button>
                  );

                  return (
                    <tr key={date} style={{
                      background:   isToday ? `${T.cyan}0A` : "transparent",
                      opacity:      isFuture ? 0.38 : 1,
                      borderBottom:`1px solid ${T.toggle_bg}`,
                    }}>

                      {/* Date */}
                      <td style={{ padding:"11px 16px" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          {isToday && <div style={{ width:6, height:6, borderRadius:"50%",
                            background:T.cyan, flexShrink:0, boxShadow:`0 0 5px ${T.cyan}` }}/>}
                          <div>
                            <div style={{ fontSize:13, fontWeight:isToday?700:500,
                              color:isToday?T.cyan:T.text, fontFamily:"JetBrains Mono,monospace" }}>
                              {date.slice(8)}{isToday ? " — Today" : ""}
                            </div>
                            <div style={{ fontSize:11, color:isWeekend?T.amber:T.dim, marginTop:1 }}>
                              {dateObj.toLocaleDateString("en-IN", { weekday:"short", month:"short" })}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td style={{ padding:"11px 16px" }}>
                        {sc ? (
                          <span style={{ fontSize:11.5, padding:"3px 10px", borderRadius:20,
                            fontWeight:600, background:sc.bg, color:sc.color }}>{rec.status}</span>
                        ) : <span style={{ fontSize:12, color:T.dim }}>—</span>}
                      </td>

                      {/* Check In */}
                      <td style={{ padding:"11px 16px" }}>
                        {tIn
                          ? timeChip(tIn, T.green)
                          : punchBtn("in", "🟢 Check In", isToday && !hasIn)}
                      </td>

                      {/* Check Out */}
                      <td style={{ padding:"11px 16px" }}>
                        {tOut
                          ? timeChip(tOut, T.red)
                          : punchBtn("out", "🔴 Check Out", isToday && hasIn && !hasOut)}
                      </td>

                      {/* Hours */}
                      <td style={{ padding:"11px 16px" }}>
                        <span style={{ fontSize:13, fontFamily:"JetBrains Mono,monospace",
                          color: !rec?.work_hours ? T.dim : rec.work_hours>=8 ? T.green : rec.work_hours>=4 ? T.amber : T.red }}>
                          {rec?.work_hours > 0 ? `${rec.work_hours}h` : "—"}
                        </span>
                      </td>

                      {/* Action */}
                      <td style={{ padding:"11px 16px" }}>
                        {isToday && hasIn && hasOut && (
                          <span style={{ fontSize:12, color:T.green, fontWeight:600 }}>✓ Day Complete</span>
                        )}
                        {isPast && !hasReg && (rec?.status==="Absent" || !rec?.check_in || !rec?.check_out) && (
                          <button onClick={() => openReg(date)}
                            style={{ fontSize:11.5, padding:"5px 12px", borderRadius:7,
                              border:`1px solid ${T.amber}40`, background:`${T.amber}0F`,
                              color:T.amber, cursor:"pointer", fontFamily:"Outfit,sans-serif",
                              fontWeight:600, whiteSpace:"nowrap" }}>
                            Request Correction
                          </button>
                        )}
                        {isPast && hasReg && (
                          <span style={{ fontSize:11, padding:"3px 9px", borderRadius:7,
                            background:`${T.amber}1A`, color:T.amber, fontWeight:600 }}>Pending</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Punch time-picker modal ── */}
      {punchModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", zIndex:9999,
          display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Outfit,sans-serif" }}
          onClick={() => setPunchModal(null)}>
          <div style={{ background:T.card, borderRadius:16, width:320, overflow:"hidden",
            border:`1px solid ${punchModal==="in" ? T.green : T.red}40`,
            boxShadow:T.shadow }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding:"16px 20px", borderBottom:`1px solid ${T.border}`,
              background: punchModal==="in" ? `${T.green}0C` : `${T.red}0C`,
              display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <Clock size={15} style={{ color: punchModal==="in" ? T.green : T.red }}/>
                <span style={{ fontSize:15, fontWeight:700, color:T.text }}>
                  {punchModal==="in" ? "Set Check-In Time" : "Set Check-Out Time"}
                </span>
              </div>
              <button onClick={() => setPunchModal(null)}
                style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}>
                <X size={15}/>
              </button>
            </div>
            {/* Picker */}
            <div style={{ padding:"20px 20px 4px" }}>
              <TimePicker value={punchTime} onChange={setPunchTime}/>
            </div>
            {/* Footer */}
            <div style={{ padding:"16px 20px", display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={() => setPunchModal(null)}
                style={{ padding:"9px 18px", borderRadius:8, border:`1px solid ${T.border}`,
                  background:T.inp_bg, color:T.muted, cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13 }}>
                Cancel
              </button>
              <button onClick={confirmPunch} disabled={punching}
                style={{ padding:"9px 22px", borderRadius:8, border:"none", cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700, color:"#fff",
                  background: punchModal==="in"
                    ? `linear-gradient(135deg,${T.green},${T.cyan})`
                    : `linear-gradient(135deg,${T.red},${T.amber})`,
                  opacity: punching ? 0.7 : 1 }}>
                {punching ? "…" : punchModal==="in" ? "🟢 Confirm Check In" : "🔴 Confirm Check Out"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Regularization modal ── */}
      {regOpen && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998,
          display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Outfit,sans-serif" }}
          onClick={() => setRegOpen(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.amber}33`, borderRadius:14, width:480, overflow:"hidden" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, background:`${T.amber}0A`,
              display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>Request Attendance Correction</h3>
              <button onClick={() => setRegOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={16}/></button>
            </div>
            <div style={{ padding:24, display:"flex", flexDirection:"column", gap:16 }}>
              <div style={{ background:`${T.amber}0F`, border:`1px solid ${T.amber}1F`, borderRadius:9,
                padding:"10px 14px", fontSize:12.5, color:T.amber }}>
                ℹ️ Use this to correct missed or wrong punch times. Your manager will review and approve.
              </div>
              <div>
                <label style={{ fontSize:11, color:T.muted, display:"block", marginBottom:5,
                  textTransform:"uppercase", letterSpacing:"0.07em" }}>Date *</label>
                <input type="date" value={form.date} max={today.toISOString().slice(0,10)}
                  onChange={e => setForm(p=>({...p, date:e.target.value}))} style={inp}/>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                <div>
                  <label style={{ fontSize:11, color:T.muted, display:"block", marginBottom:8,
                    textTransform:"uppercase", letterSpacing:"0.07em" }}>Check-in Time</label>
                  <TimePicker value={form.requested_in}
                    onChange={v => setForm(p=>({...p, requested_in:v}))}/>
                </div>
                <div>
                  <label style={{ fontSize:11, color:T.muted, display:"block", marginBottom:8,
                    textTransform:"uppercase", letterSpacing:"0.07em" }}>Check-out Time</label>
                  <TimePicker value={form.requested_out}
                    onChange={v => setForm(p=>({...p, requested_out:v}))}/>
                </div>
              </div>
              <div>
                <label style={{ fontSize:11, color:T.muted, display:"block", marginBottom:5,
                  textTransform:"uppercase", letterSpacing:"0.07em" }}>Reason *</label>
                <textarea value={form.reason} rows={3} placeholder="Explain why correction is needed…"
                  onChange={e => setForm(p=>({...p, reason:e.target.value}))}
                  style={{ ...inp, resize:"vertical" }}/>
              </div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <button onClick={() => setRegOpen(false)}
                  style={{ padding:"9px 18px", borderRadius:8, border:`1px solid ${T.toggle_bg}`,
                    background:T.inp_bg, color:T.muted, cursor:"pointer",
                    fontFamily:"Outfit,sans-serif", fontSize:13 }}>
                  Cancel
                </button>
                <button onClick={submitReg} disabled={submitting}
                  style={{ padding:"9px 18px", borderRadius:8, border:"none",
                    background:`linear-gradient(135deg,${T.amber},${T.red})`,
                    color:"#fff", cursor:"pointer", fontFamily:"Outfit,sans-serif",
                    fontSize:13, fontWeight:700, opacity:submitting ? 0.7 : 1 }}>
                  {submitting ? "Submitting…" : "Submit to Manager"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div style={{ position:"fixed", bottom:28, left:"50%", transform:"translateX(-50%)",
          padding:"10px 22px", borderRadius:10, fontSize:13, fontWeight:600, zIndex:10000,
          background:T.card, border:`1px solid ${toast.startsWith("✅") ? `${T.green}4D` : `${T.red}4D`}`,
          color:toast.startsWith("✅") ? T.green : T.red, boxShadow:T.shadow,
          fontFamily:"Outfit,sans-serif" }}>
          {toast}
        </div>
      )}
    </div>
  );
}
